import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Dumbbell, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { logEvent } from "@/lib/analytics";
import useNoIndex from "@/hooks/useNoIndex";
import { toast } from "sonner";

export default function Login() {
  useNoIndex();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    logEvent("login_click", {});
    try {
      await login(email, password);
      toast.success("Welcome back, brother.");
      navigate("/app");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Login failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain relative flex flex-col">
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 10%, rgba(255,87,34,0.18), transparent 40%), radial-gradient(circle at 90% 90%, rgba(0,229,255,0.12), transparent 40%)",
        }}
      />
      <div className="relative z-10 flex-1 flex flex-col justify-center max-w-md mx-auto w-full px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="flex items-center gap-2 mb-8">
            <div className="h-10 w-10 rounded-lg bg-primary grid place-items-center glow-orange">
              <Dumbbell size={20} className="text-white" />
            </div>
            <span className="heading text-3xl font-black tracking-tighter">
              UNLOAD<span className="text-primary">DAILY</span>
            </span>
          </div>

          <h1 className="heading text-5xl font-black tracking-tighter uppercase leading-none">
            Train your <span className="text-primary">mind</span> like your body.
          </h1>
          <p className="text-muted-foreground mt-3 mb-8">
            The private mental gym built for men. Step back into the room.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 w-full bg-[#1A1A1A] border border-white/10 text-white rounded-lg focus:border-accent focus:ring-1 focus:ring-accent p-3 outline-none transition-colors"
                placeholder="you@email.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 w-full bg-[#1A1A1A] border border-white/10 text-white rounded-lg focus:border-accent focus:ring-1 focus:ring-accent p-3 outline-none transition-colors"
                placeholder="••••••••"
              />
              <div className="text-right mt-1.5">
                <Link
                  data-testid="forgot-password-link"
                  to="/app/forgot-password"
                  className="text-xs text-muted-foreground hover:text-accent transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-white font-bold tracking-widest uppercase rounded-lg hover:shadow-[0_0_20px_rgba(255,87,34,0.5)] transition-all py-3.5 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Enter the Gym"}
            </button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            New here?{" "}
            <Link data-testid="go-register-link" to="/app/register" className="text-accent font-semibold hover:underline">
              Create your account
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
